import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  TemplateRef,
  viewChild
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TemplateWithValueDirective } from '@coin/shared/core/util-directives';
import { EnumToTextPipe, SortAlphabeticallyPipe } from '@coin/shared/core/util-pipes';
import { V2DropdownComponent } from '../../dropdown/v2-dropdown.component';
import { V2CheckboxComponent } from '../../checkbox/v2-checkbox.component';
import { V2DateInputComponent } from '../../date-input/v2-date-input.component';
import { V2TextInputComponent } from '../../text-input/v2-text-input.component';
import { V2LightweightOperatorIconComponent } from '../operator-icon/operator-icon.component';
import { V2NumberInputComponent } from '../../number-input/v2-number-input.component';
import { DropdownSearchDirective } from '../../dropdown/dropdown-search.directive';
import { defaultRuleValue, emptyRule, isValuelessOperator } from '../util/functions';
import { V2LightweightRuleEngineComponent } from '../v2-lightweight-rule-engine.component';
import {
  FREE_TEXT_OPERATORS,
  LightWeightOperator,
  LightweightRule,
  LightweightRuleEngineConfig,
  TRANSLATE_PREFIX
} from '../util/types-constants';
import { RuleEngineInput } from '@coin/shared/core/util-models';
import { TinyHelpers } from '@coin/shared/core/util-helpers';

@Component({
  selector: 'coin-lightweight-rule',
  templateUrl: './lightweight-rule.component.html',
  styleUrls: ['./lightweight-rule.component.scss'],
  imports: [
    CommonModule,
    V2DropdownComponent,
    MatIconModule,
    MatMenuModule,
    FormsModule,
    TemplateWithValueDirective,
    V2CheckboxComponent,
    V2DateInputComponent,
    V2TextInputComponent,
    V2NumberInputComponent,
    MatTooltipModule,
    TranslateModule,
    V2LightweightOperatorIconComponent,
    EnumToTextPipe,
    DropdownSearchDirective,
    SortAlphabeticallyPipe
  ],
  host: {
    '[class.readonly]': 'ruleEngine.disabled()',
    '[class.mat-elevation-z2]': 'true'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LightweightRuleComponent {
  rule = input.required<LightweightRule>();
  deletable = input(true);
  delete = output<void>();

  ruleInputTemplate = viewChild<TemplateRef<{ $implicit?: RuleEngineInput }>>('ruleInput');

  protected ruleEngine = inject(V2LightweightRuleEngineComponent);
  protected translate = inject(TranslateService);
  protected fields = computed(() => Object.keys(this.ruleEngine.config()));

  protected readonly FREE_TEXT_OPERATORS = FREE_TEXT_OPERATORS;

  get translatedField() {
    if (!this.rule().field) return '';
    if (this.ruleEngine.translateFields() === true)
      return this.translate.instant(`${TRANSLATE_PREFIX}${this.rule().field}`);
    if (this.ruleEngine.translateFields() === 'auto') return TinyHelpers.pascalcaseToText(this.rule().field);
    return this.rule().field;
  }

  get isRuleValueDisabled(): boolean {
    return !this.rule().field || !this.rule().operator;
  }

  get ruleConfig(): LightweightRuleEngineConfig[string] {
    return this.ruleEngine.config()?.[this.rule()?.field];
  }

  public setOperator(operator: LightWeightOperator) {
    if (this.rule().operator !== operator) {
      this.rule().operator = operator;
      this.rule().value = this.isValuelessOperator(operator)
        ? emptyRule().value
        : defaultRuleValue(this.ruleConfig.type);
      this.ruleEngine.registerChanges();
    }
  }

  public resetOperatorAndValue(): void {
    const { operator, value } = emptyRule();
    this.rule().operator = operator;
    this.rule().value = value;

    this.ruleEngine.registerChanges();
  }

  public onDeleteClick(): void {
    if (!this.deletable()) return;
    this.delete.emit();
  }

  public isValuelessOperator = isValuelessOperator;

  protected asRuleEngineInputType(type: unknown) {
    return type as RuleEngineInput;
  }
}
